import { GRID_TREE_DATA_GROUPING_FIELD } from '@mui/x-data-grid-pro';
import { assocPath, pathOr, times } from 'ramda';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { CustomGridTreeDataGroupingCell } from '../../cells/CustomGridTreeDataGroupingCell/CustomGridTreeDataGroupingCell';
import { HeaderCell } from '../../cells/HeaderCell/HeaderCell';
import { TypeSwitchCell } from '../../cells/TypeSwitchCell/TypeSwitchCell';
import { DemandAwardingData } from '../../DemandAwardingWidget.types';
import {
  TsrctAwardingStatusEntityVariant,
  TsrctAwardingStatusField,
  TsrctNegotiatedQuote,
  TsrctStatus,
} from '@/generated/gql/graphql';
import { I18nNamespace } from '~/common/types';
import { isNegotiationCaseAwardable } from '~/modules/DemandAwardingWidget/DemandAwardingWidget.awardability';
import { isNegotiatedQuote } from '~/util/types/isNegotiatedQuote';

import {
  AwardingDataGridGroup,
  AwardingDataGridGroupType,
  AwardingDataGridRow,
  AwardingDataGridRowType,
  EntityStatusGetter,
  UseAwardingDataGridConfig,
} from './AwardingDataGrid.types';
import { columnKey, createColumnFields } from './AwardingDataGrid.util';

const COL_MIN_WIDTH = 250;
const COL_MAX_WIDTH = 400;

const getQuoteLineItems = (quote: DemandAwardingData['quote']) => quote?.lineItems ?? [];

export const useAwardingDataGridConfig: UseAwardingDataGridConfig<AwardingDataGridRow> = ({
  negotiationCases: dataProp,
  awardingSettings,
  status: statusProp,
  awardableConfirmedQuotes,
}) => {
  const { t } = useTranslation(I18nNamespace.widgets, { keyPrefix: 'DemandAwardingWidget' });

  const getEntityStatus = useMemo<EntityStatusGetter>(() => {
    const statusMap = statusProp?.reduce(
      (acc, { entityId, entityVariant, field, status }) => {
        return assocPath([entityVariant, field, entityId], status, acc);
      },
      {} as Record<TsrctAwardingStatusEntityVariant, Record<TsrctAwardingStatusField, Record<string, TsrctStatus>>>,
    );
    return ({ entityVariant, field, entityId }) => {
      return pathOr(TsrctStatus.NEUTRAL, [entityVariant, field, entityId], statusMap);
    };
  }, [statusProp]);

  return useMemo(() => {
    const data = dataProp || [];

    const maxLineItemCount = data.reduce((acc, item) => {
      return Math.max(acc, getQuoteLineItems(item.quote).length);
    }, 0);

    const grps = Object.values(AwardingDataGridGroupType).reduce(
      (acc, key) => {
        acc[key as AwardingDataGridGroup] = t(`row.${key}`);
        return acc;
      },
      {} as { [key in AwardingDataGridGroup]: string },
    );
    return {
      columnHeaderHeight: 70,
      columns: data.map((entry, index) => ({
        display: 'flex',
        field: columnKey(index),
        minWidth: COL_MIN_WIDTH,
        maxWidth: COL_MAX_WIDTH,
        flex: 1,
        sortable: false,
        renderCell: (params) => <TypeSwitchCell {...params} />,
        renderHeader: () => (
          <HeaderCell title={entry.supplier?.name ?? ''} subTitle={t(`cell.addedBy.${entry.variant}`)} />
        ),
      })),
      rows: [
        {
          type: AwardingDataGridRowType.SUPPLIER,
          path: [grps.grpStatus, t(`row.${AwardingDataGridRowType.SUPPLIER}`)],
          ...createColumnFields(
            (item) => ({
              value: {
                id: item.supplier?.internalId,
                label: item.supplier?.externalReference,
              },
            }),
            data,
          ),
        },
        {
          type: AwardingDataGridRowType.PROCESSED_BY,
          path: [grps.grpStatus, t(`row.${AwardingDataGridRowType.PROCESSED_BY}`)],
          ...createColumnFields(
            (item) => ({
              value: {
                caseId: item.id,
                caseSequenceNumber: item.sequenceNumber,
              },
            }),
            data,
          ),
        },
        {
          type: AwardingDataGridRowType.PROCESS_STATUS,
          path: [grps.grpStatus, t(`row.${AwardingDataGridRowType.PROCESS_STATUS}`)],
          ...createColumnFields(
            (item) => ({
              status: item.status,
              processStatus: item.processStatus,
            }),
            data,
          ),
        },
        {
          type: AwardingDataGridRowType.PURCHASE_ORDERS,
          path: [grps.grpStatus, t(`row.${AwardingDataGridRowType.PURCHASE_ORDERS}`)],
          ...createColumnFields(
            (item) => ({
              values: item.sourceReferences,
            }),
            data,
          ),
        },
        {
          type: AwardingDataGridRowType.ACTION,
          path: [grps.grpStatus, t(`row.${AwardingDataGridRowType.ACTION}`)],
          ...createColumnFields(
            (item) => ({
              value: {
                caseId: item.id,
                disabled: !isNegotiationCaseAwardable({
                  status: item.status,
                  isAwardable: item.isAwardable,
                  awardableConfirmedQuotes,
                }),
                status: item.status,
                numCases: data.length,
                awardingSettings,
              },
            }),
            data,
          ),
        },
        {
          type: AwardingDataGridRowType.SUPPLIER_REFERENCE,
          path: [grps.grpQuote, t(`row.${AwardingDataGridRowType.SUPPLIER_REFERENCE}`)],
          ...createColumnFields(
            (item) => ({
              value: item.quote?.supplierReference,
            }),
            data,
          ),
        },
        {
          type: AwardingDataGridRowType.DELIVERY_TIME_FRAME,
          path: [grps.grpQuote, t(`row.${AwardingDataGridRowType.DELIVERY_TIME_FRAME}`)],
          ...createColumnFields((item) => {
            return {
              value: {
                from: item.quote?.shipmentTerms?.earlyDeliveryAt,
                to: item.quote?.shipmentTerms?.lateDeliveryAt,
              },
              status: getEntityStatus({
                entityVariant: TsrctAwardingStatusEntityVariant.QUOTE,
                field: TsrctAwardingStatusField.shipmentTerms,
                entityId: item.quote?.id,
              }),
            };
          }, data),
        },
        {
          type: AwardingDataGridRowType.INITIAL_TOTAL,
          path: [grps.grpQuote, t(`row.${AwardingDataGridRowType.INITIAL_TOTAL}`)],
          ...createColumnFields(
            (item) =>
              item.quote && {
                value: item.quote.totalValue,
                currency: item.quote.currency,
                status: getEntityStatus({
                  entityVariant: TsrctAwardingStatusEntityVariant.QUOTE,
                  field: TsrctAwardingStatusField.quotedTotalValue,
                  entityId: item.quote?.id,
                }),
              },
            data,
          ),
        },
        {
          type: AwardingDataGridRowType.DISCOUNT,
          path: [grps.grpQuote, t(`row.${AwardingDataGridRowType.DISCOUNT}`)],
          ...createColumnFields(
            (item) =>
              item.quote &&
              isNegotiatedQuote(item.quote) && {
                value: (item.quote as TsrctNegotiatedQuote).totalPriceDiscount,
                percent: (item.quote as TsrctNegotiatedQuote).discount,
                currency: item.quote.currency,
              },
            data,
          ),
        },
        {
          type: AwardingDataGridRowType.FINAL_TOTAL,
          path: [grps.grpQuote, t(`row.${AwardingDataGridRowType.FINAL_TOTAL}`)],
          ...createColumnFields(
            (item) =>
              item.quote &&
              isNegotiatedQuote(item.quote) && {
                value: (item.quote as TsrctNegotiatedQuote).totalDiscountedValue,
                currency: item.quote.currency,
                status: getEntityStatus({
                  entityVariant: TsrctAwardingStatusEntityVariant.QUOTE,
                  field: TsrctAwardingStatusField.totalDiscountedValue,
                  entityId: item.quote?.id,
                }),
              },
            data,
          ),
        },
        ...times((itemIndex) => {
          return {
            type: AwardingDataGridRowType.LINE_ITEM,
            // the path needs to be unique for mui getTreeDataPath to work
            // as we use cimode for e2e, we do not render the index within the template
            path: [grps.grpLineItems, `${t(`cell.lineItem`)} ${itemIndex + 1}`],
            ...createColumnFields((item) => {
              const lineItem = getQuoteLineItems(item.quote)[itemIndex];
              if (lineItem) {
                const negotiatedLineItem =
                  item.quote &&
                  isNegotiatedQuote(item.quote) &&
                  'totalDiscountedValue' in lineItem &&
                  'discountedPricePerUnit' in lineItem
                    ? lineItem
                    : undefined;
                return {
                  value: {
                    quotedTotalSum: lineItem.totalValue,
                    discountedTotalSum: negotiatedLineItem?.totalDiscountedValue,
                    amount: lineItem.quantity,
                    unit: lineItem.unitId,
                    currency: item.quote?.currency,
                    quotedPricePerUnit: lineItem.pricePerUnit,
                    discountedPricePerUnit: negotiatedLineItem?.discountedPricePerUnit,
                    title: lineItem.title,
                    description: lineItem.description,
                    status: getEntityStatus({
                      entityVariant: TsrctAwardingStatusEntityVariant.LINE_ITEM,
                      field: TsrctAwardingStatusField.totalDiscountedValue,
                      entityId: lineItem.id,
                    }),
                  },
                };
              }
              return null;
            }, data),
          };
        }, maxLineItemCount),

        {
          type: AwardingDataGridRowType.TOTAL_SPEND_PAST_YEAR,
          path: [grps.grpSupplier, t(`row.${AwardingDataGridRowType.TOTAL_SPEND_PAST_YEAR}`)],
          ...createColumnFields(
            (item) => ({
              value: {
                supplierId: item.supplier.internalId,
                variant: 'VOLUME',
              },
            }),
            data,
          ),
        },
        {
          type: AwardingDataGridRowType.ORDERS_PAST_YEAR,
          path: [grps.grpSupplier, t(`row.${AwardingDataGridRowType.ORDERS_PAST_YEAR}`)],
          ...createColumnFields(
            (item) => ({
              value: {
                supplierId: item.supplier.internalId,
                variant: 'COUNT',
              },
            }),
            data,
          ),
        },
      ].map((item, index) => ({ id: `${index}`, ...item })),
      initialState: {
        pinnedColumns: { left: [GRID_TREE_DATA_GROUPING_FIELD] },
      },
      isRowSelectable: (params) => {
        return params.row.type && params.row.type !== AwardingDataGridRowType.ACTION;
      },
      groupingColDef: {
        headerName: '',
        minWidth: COL_MIN_WIDTH,
        maxWidth: COL_MAX_WIDTH,
        flex: 1,
        sortable: false,
        renderCell: (params) => <CustomGridTreeDataGroupingCell {...(params as any)} />,
      },
      getTreeDataPath: (row) => row.path,
    };
  }, [dataProp, getEntityStatus, t, awardingSettings, awardableConfirmedQuotes]);
};
